import * as queryString from './QueryString';
import { getCookie, getSessionStorage, putCookie, putSessionStorage } from './LocalStorage';
import { isDesktopView } from './Utils';
import { shouldHideProfilePageSection } from './Experiments';

export const NAME = {
  HOME: 'HOME',
  GALLERY: 'GALLERY',
  IMAGE_VIEW: 'IMAGE_VIEW',
  REVIEW: 'REVIEW',
  GLOBAL_REVIEW: 'GLOBAL_REVIEW',
  CUSTOMER_ORDERS: 'CUSTOMER_ORDERS',
  CUSTOMER_ORDERS_DETAILS: 'CUSTOMER_ORDERS_DETAILS',
  RESELL: 'RESELL',
  GROUP: 'GROUP',
  CUSTOM_PAGE: 'CUSTOM_PAGE',
  RESELLER_VIEW: 'RESELLER_VIEW',
  RESELLER_PROFILE: 'RESELLER_PROFILE',
  RESELLER_CATALOGUE_HOME: 'RESELLER_CATALOGUE_HOME',
  RESELLER_CATALOGUE_GALLERY: 'RESELLER_CATALOGUE_GALLERY',
  RESELLER_IMAGE_VIEW: 'RESELLER_IMAGE_VIEW',
  RESELLER_ORDER_DETAILS: 'RESELLER_ORDER_DETAILS',
  RESELLER_GLOBAL_REVIEW: 'RESELLER_GLOBAL_REVIEW',
  RESELLER_CUSTOMER_ADD: 'RESELLER_CUSTOMER_ADD',
  ERROR: 'ERROR',
  COMPANY_DETAIL: 'COMPANY_DETAIL',
  CUSTOMER_DELETE_ACCOUNT: 'CUSTOMER_DELETE_ACCOUNT'
};

export const PATH = {
  CUSTOMER_ORDERS: '/profile/my-orders',
  CUSTOMER_ORDER_DETAILS: '/profile/my-orders/:orderId',
  CUSTOMER_DELETE_ACCOUNT: '/profile/delete-account'
};

let routeParams = {};
let currentRouteName = null;
let previousRouteName = null;
const lazyRouteComponents = {};

export const setRouteParams = (params) => {
  routeParams = params;
};

export const getRouteParams = () => routeParams;

export const addToCartRoutes = {
  [NAME.GALLERY]: true,
  [NAME.RESELLER_CATALOGUE_GALLERY]: true,
  [NAME.HOME]: true,
  [NAME.GROUP]: true,
  [NAME.CUSTOM_PAGE]: true,
  [NAME.CUSTOMER_ORDERS]: true,
  [NAME.CUSTOMER_ORDERS_DETAILS]: true,
  [NAME.COMPANY_DETAIL]: true
};

export const shouldShowCustomerProfileButton = () => {
  if (!isDesktopView() || shouldHideProfilePageSection()) {
    return false;
  }

  const currentRouteName = getCurrentRouteName();

  return (
    isCustomerOrdersRoute(currentRouteName) ||
    {
      [NAME.GALLERY]: true,
      [NAME.HOME]: true,
      [NAME.GROUP]: true,
      [NAME.CUSTOM_PAGE]: true,
      [NAME.GLOBAL_REVIEW]: true,
      [NAME.COMPANY_DETAIL]: true
    }[currentRouteName]
  );
};

export const isSingleRoute = (route) => route === NAME.HOME;

export const isResellSingleRoute = (route) => route === NAME.RESELL;

export const isGalleryRoute = (route) => route === NAME.GALLERY;

export const isImageRoute = (route) => route === NAME.IMAGE_VIEW;

export const isCompanyDetailRoute = (route) => route === NAME.COMPANY_DETAIL;

export const isGroupRoute = (route) => route === NAME.GROUP || route === NAME.CUSTOM_PAGE;

export const isReviewRoute = (route) => route === NAME.GLOBAL_REVIEW;

export const isCustomerOrdersRoute = (route) =>
  route === NAME.CUSTOMER_ORDERS || route === NAME.CUSTOMER_ORDERS_DETAILS || route === NAME.CUSTOMER_DELETE_ACCOUNT;

export const isCustomerDeleteAccountRoute = (route) => route === NAME.CUSTOMER_DELETE_ACCOUNT;

export const isResellerViewRoute = (route) =>
  isResellerAmalgamRequiredRoute(route) ||
  isResellerSingleAmalgamRequiredRoute(route) ||
  route === NAME.RESELLER_GLOBAL_REVIEW ||
  route === NAME.RESELLER_CUSTOMER_ADD;

export const isResellerAmalgamRequiredRoute = (route) =>
  route === NAME.RESELLER_VIEW ||
  route === NAME.RESELLER_ORDER_DETAILS ||
  route === NAME.RESELLER_PROFILE;

export const isResellerSingleAmalgamRequiredRoute = (route) =>
  route === NAME.RESELLER_CATALOGUE_HOME ||
  route === NAME.RESELLER_CATALOGUE_GALLERY ||
  route === NAME.RESELLER_IMAGE_VIEW;

export const getRouteQuery = () => queryString.parse(location.search);

export const setCurrentRouteName = (routeName) => {
  currentRouteName = routeName;
};

export const setPreviousRouteName = (routeName) => {
  previousRouteName = routeName;
};

export const shouldClearFiltersOnRouteChange = (currentRoute) =>
  currentRoute === NAME.HOME || currentRoute === NAME.RESELLER_CATALOGUE_HOME;

export const getCurrentRouteName = () => currentRouteName;

export const getPreviousRouteName = () => previousRouteName;

export const isLazyComponentFetched = (key) => !!lazyRouteComponents[key];

export const setLazyComponent = (key, component) => {
  lazyRouteComponents[key] = component;
};

export const getLazyComponent = (key) => lazyRouteComponents[key];

export const importSingleBundle = () =>
  import(/* webpackChunkName: "single" */ '../pages/SingleCatalogue');

export const importResellSingleBundle = () =>
  import(/* webpackChunkName: "resell-single" */ '../pages/ResellPage');

export const importGalleryPageBundle = () =>
  import(/* webpackChunkName: "gallery" */ '../pages/GalleryPage');

export const importImagePageBundle = () =>
  import(/* webpackChunkName: "image" */ '../pages/ImageView');

export const importCompanyDetailBundle = () =>
  import(/* webpackChunkName: "company-detail" */ '../pages/CompanyDetail');

export const importGroupBundle = () => import(/* webpackChunkName: "group" */ '../pages/GroupPage');

export const importCustomPageBundle = () =>
  import(/* webpackChunkName: "custom-page" */ '../pages/CustomPage');

export const importCustomerOrdersBundle = () =>
  import(/* webpackChunkName: "customer-orders" */ '../pages/CustomerOrders');

export const importGlobalReviewBundle = () =>
  import(/* webpackChunkName: "global-review" */ '../pages/GlobalReview');

export const importResellerViewBundle = () =>
  import(/* webpackChunkName: "resellerview" */ '../pages/ResellerView');

export const setLastVisitedSingleCatalogueLink = (companyId) => {
  putCookie('last_single', location.href);
  putCookie('last_single_companyId', companyId);
};

export const getLastVisitedSingleCatalogueLink = () => getCookie('last_single');
export const getLastVisitedSingleCatalogueCompanyId = () => getCookie('last_single_companyId');

export const setLastVisitedGroupCatalogueLink = (companyId) => {
  putCookie('last_group', location.href);
  putCookie('last_group_companyId', companyId);
};

export const getLastVisitedGroupCatalogueLink = () => getCookie('last_group');
export const getLastVisitedGroupCatalogueCompanyId = () => getCookie('last_group_companyId');

export const getPrevCatalogueRoute = ({ route = '', previousRoute } = {}) => {
  if (previousRoute === NAME.GALLERY) {
    const [, type, , companySlug, catalogueSlug, randomSlug] = route.split('/');
    return `/${type}/${companySlug}/${catalogueSlug}/${randomSlug}`;
  }

  if (previousRoute === NAME.CUSTOM_PAGE) {
    return '/';
  }

  return route;
};

export const setLastVisitedHomeOrCatalogueRoute = ({
  route,
  wasGroupRoute,
  wasSingleRoute
} = {}) => {
  putSessionStorage('last_visited_home_or_catalogue_route', route);
  putSessionStorage('last_visited_group_route', wasGroupRoute);
  putSessionStorage('last_visited_single_route', wasSingleRoute);
};

export const getLastVisitedHomeOrCatalogueRoute = () => ({
  route: getSessionStorage('last_visited_home_or_catalogue_route'),
  wasGroupRoute: getSessionStorage('last_visited_group_route'),
  wasSingleRoute: getSessionStorage('last_visited_single_route')
});

export const isGalleryView = () => getCurrentRouteName() === NAME.GALLERY;

export const isGalleryImageRoute = (route) =>
  route === NAME.GALLERY ||
  route === NAME.IMAGE_VIEW ||
  route === NAME.RESELLER_CATALOGUE_GALLERY ||
  route === NAME.RESELLER_IMAGE_VIEW;

export const isImageViewRoute = (route) =>
  route === NAME.RESELLER_IMAGE_VIEW || route === NAME.IMAGE_VIEW;

export const isGlobalReviewRoute = (route) =>
  route === NAME.GLOBAL_REVIEW || route === NAME.RESELLER_GLOBAL_REVIEW;

export const isResellerReviewRoute = (route) => route === NAME.RESELLER_GLOBAL_REVIEW;

export const shouldShowLightThemeOnRoute = (currentRoute) =>
  isGalleryView() || isReviewRoute(currentRoute) || isCustomerOrdersRoute(currentRoute);

export const isResellerRoute = () => {
  const currentRoute = getCurrentRouteName();
  if (currentRoute) {
    // TODO: hide menubar in reseller view
    return false;
  }

  return (
    currentRoute === NAME.RESELLER_GLOBAL_REVIEW ||
    currentRoute === NAME.RESELLER_CATALOGUE_GALLERY ||
    currentRoute === NAME.RESELLER_CATALOGUE_HOME ||
    currentRoute === NAME.RESELLER_ORDER_DETAILS ||
    currentRoute === NAME.RESELLER_CUSTOMER_ADD ||
    currentRoute === NAME.RESELLER_IMAGE_VIEW ||
    currentRoute === NAME.RESELLER_PROFILE ||
    currentRoute === NAME.RESELLER_VIEW
  );
};
